import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './HomePage.css';

function HomePage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get('/api/content');
      setContent(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching content:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!content) {
    return <div className="error">Error loading content</div>;
  }

  return (
    <div className="homepage">
      <header className="header">
        <nav className="navbar">
          <div className="container">
            <div className="nav-content">
              <h1 className="logo">Chai High</h1>
              <button className="hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <span></span>
                <span></span>
                <span></span>
              </button>
              <ul className={`nav-links ${mobileMenuOpen ? 'nav-links-mobile' : ''}`}>
                <li><a href="#home" onClick={() => setMobileMenuOpen(false)}>Home</a></li>
                <li><a href="#about" onClick={() => setMobileMenuOpen(false)}>About</a></li>
                <li><a href="#menu" onClick={() => setMobileMenuOpen(false)}>Menu</a></li>
                <li><a href="#contact" onClick={() => setMobileMenuOpen(false)}>Contact</a></li>
              </ul>
            </div>
          </div>
        </nav>
      </header>

      <section id="home" className="hero" style={{ backgroundImage: `url(${content.heroSection.backgroundImage})` }}>
        <div className="hero-overlay">
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title">{content.heroSection.title}</h1>
              <p className="hero-subtitle">{content.heroSection.subtitle}</p>
              <a href="#menu" className="btn hero-btn">Explore Menu</a>
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="about section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>{content.about.title}</h2>
              <div className="about-description">
                {content.about.description.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="about-image">
              <img src={content.about.image} alt="About Chai High" />
            </div>
          </div>
        </div>
      </section>

      <section className="specialties section">
        <div className="container">
          <h2 className="section-title">Why Choose Us</h2>
          <div className="specialties-grid">
            {content.specialties.map((specialty, index) => (
              <div key={index} className="specialty-card">
                <div className="specialty-icon">{specialty.icon}</div>
                <h3>{specialty.title}</h3>
                <p>{specialty.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="menu" className="menu section">
        <div className="container">
          <h2 className="section-title">{content.menu.title}</h2>
          <div className="menu-grid">
            {content.menu.items.map((item, index) => (
              <div key={index} className="menu-card">
                <img src={item.image} alt={item.name} className="menu-image" />
                <div className="menu-content">
                  <h3>{item.name}</h3>
                  <p className="menu-description">{item.description}</p>
                  <div className="menu-footer">
                    <span className="menu-category">{item.category}</span>
                    <span className="menu-price">{item.price}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="contact section">
        <div className="container">
          <h2 className="section-title">Get In Touch</h2>
          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-item">
                <h3>📞 Phone</h3>
                <p>{content.contact.phone}</p>
              </div>
              <div className="contact-item">
                <h3>📧 Email</h3>
                <p>{content.contact.email}</p>
              </div>
              <div className="contact-item">
                <h3>📍 Address</h3>
                <p>{content.contact.address}</p>
              </div>
              <div className="contact-item">
                <h3>🕒 Hours</h3>
                <p>{content.contact.hours}</p>
              </div>
            </div>
            <div className="contact-cta">
              <h3>Order Your Tiffin Today!</h3>
              <p>Experience the taste of home-cooked meals delivered to your doorstep.</p>
              <a href={`tel:${content.contact.phone}`} className="btn">Call Now</a>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <p>&copy; 2024 Chai High. All rights reserved.</p>
            <div className="social-links">
              {content.social.facebook && <a href={content.social.facebook} target="_blank" rel="noopener noreferrer">Facebook</a>}
              {content.social.instagram && <a href={content.social.instagram} target="_blank" rel="noopener noreferrer">Instagram</a>}
              {content.social.twitter && <a href={content.social.twitter} target="_blank" rel="noopener noreferrer">Twitter</a>}
            </div>
          </div>
        </div>
      </footer>
      <a 
        href={`https://wa.me/${content.contact.phone.replace(/[^0-9]/g, '')}?text=Hi, I would like to order from Chai High`}
        className="whatsapp-widget"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contact us on WhatsApp"
      >
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
}

export default HomePage;